import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Checkbox, Collapse, IconButton } from '@mui/material';
import { ExpandMore, ChevronRight, LocalOffer } from '@mui/icons-material';
import { getTagColor } from 'Core/data/Helpers';
import './index.scss';

const TreeNode = ({
  node,
  level = 0,
  isSearching,
  selectedTagIds,
  onTagSelect,
  onTagDeselect,
}) => {
  // When searching, expand nodes by default to show matching children
  // Otherwise start collapsed and preserve user toggles while dropdown stays open
  const [expanded, setExpanded] = React.useState(isSearching);

  React.useEffect(() => {
    if (isSearching) {
      setExpanded(true);
    }
  }, [isSearching]);

  const hasChildren = node.children?.length > 0;
  const isSelected = selectedTagIds.has(String(node.id));
  const tagColor = getTagColor(node.color);
  const selectionPayload = {
    id: String(node.id),
    label: node.label,
    color: node.color,
    tagPath: node.tagPath,
  };

  const handleToggleSelection = () => {
    if (isSelected) {
      onTagDeselect?.(selectionPayload);
      return;
    }

    onTagSelect?.(selectionPayload);
  };

  const handleToggleExpand = (event) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <div className="tag-tree-node">
      <div
        className={`tag-tree-item ${isSelected ? 'selected' : ''} ${node._matches ? 'matches' : ''}`}
        style={{ paddingLeft: level * 20 }}
        onClick={handleToggleSelection}
      >
        {hasChildren ? (
          <IconButton size="small" onClick={handleToggleExpand} className="expand-button">
            {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </IconButton>
        ) : (
          <Box className="expand-placeholder" />
        )}
        <Checkbox
          size="small"
          checked={isSelected}
          disabled={isSelected && !onTagDeselect}
          onClick={(event) => event.stopPropagation()}
          onChange={handleToggleSelection}
        />
        <LocalOffer
          fontSize="small"
          className="tag-icon"
          style={{ color: tagColor ? `#${tagColor}` : '#9e9e9e' }}
        />
        <Typography
          variant="body2"
          className="tag-label"
          title={node.label}
        >
          {node.label}
        </Typography>
      </div>
      {hasChildren && (
        <Collapse in={expanded}>
          <div className="tag-tree-children">
            {node.children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                level={level + 1}
                isSearching={isSearching}
                selectedTagIds={selectedTagIds}
                onTagSelect={onTagSelect}
                onTagDeselect={onTagDeselect}
              />
            ))}
          </div>
        </Collapse>
      )}
    </div>
  );
};

/**
 * Custom TagTreeFilter component for filtering tags hierarchically
 * Displays tags in a tree structure with search filtering support
 */
const TagTreeFilter = ({
  tags = [],
  searchText = '',
  noResultsLabel,
  onTagSelect,
  onTagDeselect,
  selectedTagIds = new Set(),
  height = 280,
}) => {
  // Build tree structure from flat tags array using parentId
  const tagTree = useMemo(() => {
    if (!tags?.length) return [];

    // Create a map of tags by ID for quick lookup
    const tagMapById = new Map();
    tags.forEach((tag) => {
      tagMapById.set(String(tag.id), { ...tag, children: [] });
    });

    // Build tree by assigning children to parents based on parentId
    const roots = [];
    tags.forEach((tag) => {
      const tagNode = tagMapById.get(String(tag.id));
      if (!tagNode) return;

      const parentId = tag.parentId;

      if (!parentId || parentId === 0 || parentId === '0') {
        // Root level tag (no parent)
        roots.push(tagNode);
      } else {
        // Find parent by parentId
        const parent = tagMapById.get(String(parentId));
        if (parent) {
          parent.children.push(tagNode);
        } else {
          // Parent not in list, treat as root for display purposes
          roots.push(tagNode);
        }
      }
    });

    // Sort roots and children alphabetically by label
    const sortByLabel = (a, b) => (a.label || '').localeCompare(b.label || '');
    roots.sort(sortByLabel);
    const sortChildren = (nodes) => {
      nodes.forEach((node) => {
        if (node.children?.length) {
          node.children.sort(sortByLabel);
          sortChildren(node.children);
        }
      });
    };
    sortChildren(roots);

    return roots;
  }, [tags]);

  // Filter tags based on search text
  const filteredTree = useMemo(() => {
    if (!searchText?.trim()) return tagTree;

    const searchLower = searchText.trim().toLowerCase();

    // Recursive function to filter tree and keep ancestors of matches
    const filterNode = (node) => {
      const labelMatches = node.label?.toLowerCase().includes(searchLower);
      const filteredChildren = node.children
        .map(filterNode)
        .filter(Boolean);

      // Include node if it matches or has matching descendants
      if (labelMatches || filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren,
          _matches: labelMatches, // Mark if this node directly matches
        };
      }
      return null;
    };

    return tagTree.map(filterNode).filter(Boolean);
  }, [tagTree, searchText]);

  // Check if we're in search mode
  const isSearching = Boolean(searchText?.trim());

  if (filteredTree.length === 0) {
    return (
      <Box className="tag-tree-filter empty" style={{ height }}>
        <Typography variant="body2" color="textSecondary">
          {searchText?.trim() ? (noResultsLabel || 'No matching tags found') : 'No tags available'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="tag-tree-filter" style={{ maxHeight: height, overflowY: 'auto' }}>
      {filteredTree.map((node) => (
        <TreeNode
          key={node.id}
          node={node}
          isSearching={isSearching}
          selectedTagIds={selectedTagIds}
          onTagSelect={onTagSelect}
          onTagDeselect={onTagDeselect}
        />
      ))}
    </Box>
  );
};

TagTreeFilter.propTypes = {
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      tagPath: PropTypes.string,
      color: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  searchText: PropTypes.string,
  noResultsLabel: PropTypes.string,
  onTagSelect: PropTypes.func,
  onTagDeselect: PropTypes.func,
  selectedTagIds: PropTypes.instanceOf(Set),
  height: PropTypes.number,
};

export default TagTreeFilter;
